import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { WaitlistForm } from '../components/WaitlistForm';
import { FadeIn } from '../components/animations/FadeIn';

function QA() {
  const [openQuestion, setOpenQuestion] = React.useState<number | null>(null);
  const [submitted, setSubmitted] = React.useState(false);

  const questions = [
    {
      question: "Vad är Utlysningslabbet?",
      answer: "Utlysningslabbet är en idé för en AI-driven plattform som gör det enklare för startups, entreprenörer och forskare att hitta och söka rätt bidrag. Vår AI analyserar ditt projekt och matchar det mot aktiva utlysningar och tusentals tidigare framgångsrika ansökningar."
    },
    {
      question: "Vem står bakom idén till Utlysningslabbet?",
      answer: "Vi är en liten grupp entreprenörer och yrkesverksamma som själva har upplevt utmaningarna med att hitta och söka bidrag i våra jobb. Efter att ha spenderat otaliga timmar på att researcha utlysningar och skriva ansökningar insåg vi att det måste finnas ett bättre sätt. Nu vill vi se om fler delar denna utmaning och skulle vara intresserade av att inte bara använda tjänsten, utan också hjälpa oss utveckla den. Vi tror att genom att involvera framtida användare tidigt i processen kan vi bygga något som verkligen löser de utmaningar som finns."
    },
    {
      question: "Hur fungerar det?",
      answer: "Utlysningslabbet använder avancerad AI för att analysera ditt projekt och matcha det mot relevanta bidrag. AI-assistenten guidar dig genom hela ansökningsprocessen med insikter från tidigare framgångsrika ansökningar och ger konkreta förbättringsförslag för att maximera dina chanser att få finansiering."
    },
    {
      question: "Hur kan AI-assistenten hjälpa mig?",
      answer: "Vår AI-assistent är unikt tränad på svenska utlysningstexter och har tillgång till omfattande data från över 60 000 framgångsrika projekt genom integrationer med öppna API:er för bidragsdata i Sverige. Detta ger assistenten möjlighet att analysera:\n\n- Historiska data från ledande finansiärer\n- Projektbeskrivningar och beviljade belopp\n- Detaljerad information om utlysningar och framgångsrika ansökningar\n\nDetta gör att assistenten kan ge mycket specifika och relevanta råd baserade på verkliga, framgångsrika projekt inom din sektor."
    },
    {
      question: "Hur fungerar det tidiga tillgångsprogrammet?",
      answer: "Vi har skapat ett unikt program för tidiga användare med tre nivåer av exklusiva fördelar:\n\n1. Grundare (första 500 användare):\n- 3 månader gratis användning\n- Livstids VIP-support med prioriterad hjälp\n- Tidig åtkomst till nya funktioner före alla andra\n- Möjlighet att påverka produktens utveckling\n\n2. Pionjärer (användare 501-1000):\n- 2 månader gratis användning\n- Prioriterad support\n- Tidig åtkomst till nya funktioner\n\n3. Tidiga användare (användare 1001-2000):\n- 1 månad gratis användning\n- Tidig åtkomst till nya funktioner\n\nVarje nivå har ett begränsat antal platser och fördelarna är livstids, vilket betyder att supportnivån följer med ditt konto permanent."
    },
    {
      question: "När lanseras Utlysningslabbet?",
      answer: "Vi kommer endast att bygga det om vi ser att det finns ett intresse: Vi utvecklar plattformen när vi når 2000 intresseanmälningar på väntelistan. Just nu fokuserar vi på att validera konceptet och samla feedback från potentiella användare för att säkerställa att vi bygger något som verkligen löser era utmaningar."
    },
    {
      question: "Vilka typer av bidrag kan man hitta?",
      answer: "Utlysningslabbet kommer att täcka ett brett spektrum av bidrag från svenska och europeiska finansiärer, inklusive innovationsstöd, forskningsanslag, och utvecklingsbidrag. Detta inkluderar bidrag från organisationer som Vinnova, Tillväxtverket, EU:s ramprogram, och många fler."
    },
    {
      question: "Hur skiljer sig Utlysningslabbet från andra tjänster?",
      answer: "Det unika med Utlysningslabbet är att vi hjälper dig spara värdefull tid genom att automatisera research och ansökningsprocessen. Vår AI hittar inte bara relevanta bidrag utan guidar dig genom hela processen med konkreta förbättringsförslag baserade på framgångsrika ansökningar. Detta gör att du kan fokusera mer tid på att utveckla ditt projekt och mindre tid på att researcha och skriva ansökningar."
    },
    {
      question: "Kan jag testa tjänsten redan nu?",
      answer: "På demo-sidan kan du se ett exempel på hur vi tänker oss att AI-assistenten skulle kunna fungera. Detta är bara en konceptdemonstration för att ge en känsla av visionen - den faktiska tjänsten kommer att utvecklas baserat på feedback och behov från våra framtida användare. Om vi når 2000 intresseanmälningar kommer vi att börja bygga den riktiga versionen, och då får väntelistans medlemmar vara med och påverka hur tjänsten ska fungera, med prioriterad åtkomst för Grundare och Pionjärer."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
          <FadeIn>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Frågor och svar
            </h1>
            <p className="text-lg text-gray-600">
              Här hittar du svar på de vanligaste frågorna om Utlysningslabbet.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Q&A Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-4">
            {questions.map((q, index) => (
              <FadeIn key={index} delay={index * 0.1}>
                <div
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md"
                >
                  <button
                    onClick={() => setOpenQuestion(openQuestion === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between"
                  >
                    <span className="text-lg font-medium text-gray-900">{q.question}</span>
                    <ChevronDown
                      size={20}
                      className={`text-gray-500 transition-transform duration-200 ${
                        openQuestion === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`px-6 transition-all duration-200 ${
                      openQuestion === index ? 'py-4 border-t border-gray-100' : 'py-0 h-0 overflow-hidden'
                    }`}
                  >
                    <p className="text-gray-600 whitespace-pre-line">{q.answer}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-4">
          {!submitted ? (
            <FadeIn>
              <div className="bg-gray-900 p-8 rounded-2xl text-center">
                <h2 className="text-2xl font-bold text-white mb-4">
                  Har du fler frågor?
                </h2>
                <p className="text-gray-300 mb-8">
                  Gå med i väntelistan för att få de senaste uppdateringarna om utvecklingen och vara bland de första att testa Utlysningslabbet.
                </p>
                <div className="max-w-md mx-auto">
                  <WaitlistForm 
                    onSuccess={() => setSubmitted(true)}
                    buttonText="Håll mig uppdaterad"
                  />
                </div>
              </div>
            </FadeIn>
          ) : (
            <FadeIn>
              <div className="bg-green-50 p-8 rounded-2xl border border-green-100 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Tack för ditt intresse!
                </h2>
                <p className="text-gray-600">
                  Vi håller dig uppdaterad om projektets utveckling.
                </p>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}

export default QA;